import React, { useState, useEffect, useRef } from 'react';
import { User2 } from 'lucide-react';
import { useInterview } from '../../contexts/InterviewContext';
import { Message } from '../../../../types/interviews';
import SpeakingIndicator from '../video/SpeakingIndicator';

interface SpeakingBubbleProps {
  active: boolean;
  companyLogo?: string;
}

const SpeakingBubble: React.FC<SpeakingBubbleProps> = ({ active, companyLogo }) => {
  if (!active) return null;
  
  return (
    <div className="absolute inset-0 rounded-full animate-pulse z-0">
      <div className="absolute inset-[-4px] rounded-full bg-primary-light/20 dark:bg-primary-dark/20 animate-ping-slow"></div>
      <div className="absolute inset-[-8px] rounded-full bg-primary-light/10 dark:bg-primary-dark/10 animate-ping-slow animation-delay-200"></div>
      <div className="absolute inset-[-12px] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 animate-ping-slow animation-delay-400"></div>
    </div>
  );
};

interface AIInterviewerProps {
  onClick?: () => void;
  isSmallView?: boolean; // Used to determine if the view is small and we should show a smaller avatar
  isMobileView?: boolean; // Used to determine if the view is mobile and we should show a smaller avatar
  isAISpeaking?: boolean; // Used to determine if the AI is speaking and we should show the speaking indicator
}

const AIInterviewer: React.FC<AIInterviewerProps> = ({ 
  onClick,
  isSmallView = false,
  isMobileView = false,
  isAISpeaking = false,
}) => {
  const { 
    isSpeaking, 
    messages,
    modelTranscript,
    aiSpeakingRef,
    isInterviewerThinking,
    interviewDetails
  } = useInterview();

  // State to track typing animation
  const [isTyping, setIsTyping] = useState(false);
  const [prevModelTranscript, setPrevModelTranscript] = useState('');
  
  // Refs to track animation timers
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastAnimationTimeRef = useRef<number>(0);

  // Only render the most recent AI message if available
  const latestAIMessage : Message | undefined = messages.length > 0 
    ? messages.filter((m: Message) => m.sender === 'ai').pop() 
    : undefined;

  // Monitor changes to modelTranscript for typing animation with improved controls
  useEffect(() => {
    // Clear any existing timer to prevent overlap
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    
    // Check if there's a meaningful change to animate
    if (modelTranscript && modelTranscript !== prevModelTranscript) {
      // Record when we started this animation
      lastAnimationTimeRef.current = Date.now();
      
      // Start the animation
      setIsTyping(true);
      
      // Set timer to end animation after fixed duration
      typingTimerRef.current = setTimeout(() => {
        setIsTyping(false);
        typingTimerRef.current = null;
      }, 800); // Slightly longer animation for better visibility
      
      // Update previous value
      setPrevModelTranscript(modelTranscript);
    } else if (!modelTranscript && prevModelTranscript) {
      // If transcript was cleared, remove typing animation immediately
      setIsTyping(false);
      setPrevModelTranscript('');
    }
    
    // Cleanup on unmount or dependency change
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [modelTranscript, prevModelTranscript]);
  
  // Extra effect to ensure typing animation stops when AI stops speaking
  useEffect(() => {
    // If AI is no longer speaking but typing animation is still active
    if (!isAISpeaking && !aiSpeakingRef?.current && isTyping) {
      // Only force-stop if it's been at least 1 second since the last animation started
      // This prevents flickering during short pauses in speech
      const timeSinceLastAnimation = Date.now() - lastAnimationTimeRef.current;
      if (timeSinceLastAnimation > 1000) {
        setIsTyping(false);
      }
    }
  }, [isAISpeaking, aiSpeakingRef, isTyping]);

  // Determine what message to display
  const displayMessage = () => {
    // If AI is thinking but not yet speaking, show thinking message
    if (isInterviewerThinking && !modelTranscript?.trim() && !isAISpeaking) {
      return 'thinking'; // Special keyword to show thinking animation
    }
    
    // If AI is speaking and we have real-time transcript, always show it
    if (modelTranscript && modelTranscript.trim()) {
      return modelTranscript;
    }
    
    // If we have a latest message, use that
    if (latestAIMessage && latestAIMessage.text) {
      return latestAIMessage.text;
    }
    
    // Default to empty string
    return '';
  };

  // Check if there's any message content to display
  const hasMessageContent = Boolean(displayMessage() || isAISpeaking || isInterviewerThinking);

  return (
    <div 
      className={`relative ${isSmallView 
        ? 'w-full h-full flex items-center justify-center  bg-gradient-to-br from-background-light to-surface-light dark:from-background-dark dark:to-surface-dark' 
        : 'w-full h-full bg-gradient-to-br from-background-light to-surface-light dark:from-background-dark dark:to-surface-dark flex flex-col items-center justify-center p-6 fade-zoom-in'}`}
      onClick={onClick}
    >
      {/* Interviewer Avatar container */}
      <div className="relative">
        {/* Avatar circle */}
        <div className={` ${(isMobileView && isSmallView) || isSmallView
          ? 'w-14 h-14' 
          : 'w-28 h-28 md:w-32 md:h-32'} rounded-full bg-primary-light/10 dark:bg-primary-dark/10 border-${isSmallView ? '0' : '0'} border-primary-light/20 dark:border-primary-dark/20 flex items-center justify-center overflow-hidden relative`}>
          {/* Pulsing bubble behind avatar when speaking */}
          <SpeakingBubble active={isSpeaking} />
          
          {/* Default avatar icon/image */}
          <div className={`absolute inset-0 flex items-center justify-center z-10 ${interviewDetails?.companyLogo ? `bg-background-light dark:bg-background-light ${isSmallView ? 'p-3' : 'p-4'}` : ''}`}>
            {interviewDetails?.companyLogo ? (
              <img 
                src={interviewDetails.companyLogo} 
                height={isSmallView ? 32 : 64} 
                width={isSmallView ? 32 : 64} 
                alt="Company Logo" 
                className="w-full h-full object-contain" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary-light dark:bg-primary-dark rounded-full">
                <span className="text-white text-2xl md:text-4xl font-bold font-heading">
                  {((interviewDetails?.company || 'J').charAt(0).toUpperCase())}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Latest speech bubble - only show in full view */}
      {!isSmallView && hasMessageContent && (
        <div className="relative max-w-2xl mt-4 pl-4 py-4 pr-2 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg border border-primary-light/20 dark:border-primary-dark/20 shadow-sm slide-in-left max-h-[25vh] overflow-hidden">
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-light/10 dark:bg-primary-dark/10 border-l border-t border-primary-light/20 dark:border-primary-dark/20 rotate-45"></div>
          <div className="max-h-full overflow-y-auto scrollbar-thin scrollbar-thumb-primary-light/20 dark:scrollbar-thumb-primary-dark/20 scrollbar-track-transparent hover:scrollbar-thumb-primary-light/30 dark:hover:scrollbar-thumb-primary-dark/30">
            <p className={`text-text-primary-light dark:text-text-primary-dark text-base break-words`}>
              {displayMessage() === 'thinking' ? (
                <span className="text-text-secondary-light dark:text-text-secondary-dark italic">
                  Thinking
                  <span className="inline-flex space-x-1 ml-2">
                    <span className="h-1.5 w-1.5 bg-text-secondary-light/60 dark:bg-text-secondary-dark/60 rounded-full animate-bounce"></span>
                    <span className="h-1.5 w-1.5 bg-text-secondary-light/60 dark:bg-text-secondary-dark/60 rounded-full animate-bounce animation-delay-200"></span>
                    <span className="h-1.5 w-1.5 bg-text-secondary-light/60 dark:bg-text-secondary-dark/60 rounded-full animate-bounce animation-delay-400"></span>
                  </span>
                </span>
              ) : (
                displayMessage()
              )}
              {isAISpeaking && !displayMessage() && (
                <span className="inline-flex space-x-1 ml-1">
                  <span className="h-2 w-2 bg-primary-light/50 dark:bg-primary-dark/50 rounded-full animate-bounce"></span>
                  <span className="h-2 w-2 bg-primary-light/50 dark:bg-primary-dark/50 rounded-full animate-bounce animation-delay-200"></span>
                  <span className="h-2 w-2 bg-primary-light/50 dark:bg-primary-dark/50 rounded-full animate-bounce animation-delay-400"></span>
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* AI speaking indicator */}
      {isAISpeaking && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <SpeakingIndicator type="ai" isSmallView={isSmallView} />
        </div>
      )}
    </div>
  );
};

export default AIInterviewer; 